import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PageMeta } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { TryItNowWidget } from '@/components/TryItNowWidget';
import {
  Sparkles, Camera, Video, Globe, MessageSquare, Mail, Share2, MapPin,
  Users, Megaphone, Sofa, FileText, ArrowRight, Crown, Lightbulb,
} from 'lucide-react';

const ideas = [
  { icon: Camera, title: '1. Upgrade every photo with AI enhancement', desc: 'Declutter, golden-hour lighting, sky replace, lawn refresh. Polished photos out-click raw shots 2-to-1.' },
  { icon: Sofa, title: '2. Virtually stage empty rooms', desc: 'Empty rooms feel cold. AI staging adds tasteful furniture in seconds — buyers picture themselves living there.' },
  { icon: Video, title: '3. Post a 30-second slideshow video', desc: 'Reels and TikToks outperform static photos on every social channel. Auto-generate one from your listing photos.' },
  { icon: Globe, title: '4. Send a microsite, not a PDF', desc: 'A clean property page (with photos, video, description, map, contact) feels premium and shares like a link.' },
  { icon: FileText, title: '5. Write descriptions for each platform', desc: 'Instagram needs 80 words. Premium portals need 300. Generate the right version for each channel — automatically.' },
  { icon: MessageSquare, title: '6. Translate listings into local languages', desc: 'International buyers convert better in their own language. 16-language translation in one click.' },
  { icon: Mail, title: '7. Build a weekly "just listed" email', desc: 'Past clients and your sphere convert higher than cold leads. Send one email a week with new microsite links.' },
  { icon: Share2, title: '8. Use Stories, not just feed posts', desc: 'Instagram Stories and Facebook Stories get higher reach for low-follower agents. Generate Stories-format copy + crops.' },
  { icon: MapPin, title: '9. Lead with the neighborhood, not the house', desc: 'Buyers search by area first. Reels showing local cafés, schools, and beaches drive saves and DMs.' },
  { icon: Users, title: '10. Spotlight past client wins', desc: 'A 15-second "just sold for €X over asking" video is the highest-performing content for sphere marketing.' },
  { icon: Megaphone, title: '11. Boost top posts, not every post', desc: 'Spend €10–20 to promote your single best listing reel each week. Beats spraying ads across average content.' },
  { icon: Lightbulb, title: '12. Use QR codes on yard signs', desc: 'Point to the listing microsite. Walk-by traffic converts to viewings without you on the phone.' },
];

const faqs = [
  {
    q: 'What is the most effective real estate marketing strategy in 2026?',
    a: 'Multi-channel content from a single listing: enhanced photos, short-form video, a shareable microsite, and platform-specific captions. Tools like ListBright generate all of these from one upload in about 10 minutes.',
  },
  {
    q: 'How can I market real estate on a low budget?',
    a: 'Focus on three free/cheap channels: Instagram Reels, sphere-of-influence email, and listing microsites you can text directly. A $15 single-property kit can replace $150+ of freelance work.',
  },
  {
    q: 'What kind of content do top real estate agents post?',
    a: 'A mix of listing reels, neighborhood walks, "just sold" wins, and behind-the-scenes day-in-the-life. Consistency beats production value — post 3–5 times a week.',
  },
  {
    q: 'How do I get more real estate leads from social media?',
    a: 'Post short-form video consistently, always include a clear next step (DM, link in bio, microsite QR), and reply to every comment within an hour. Paid boosts on your top posts amplify what already works.',
  },
  {
    q: 'Are AI tools worth it for real estate marketing?',
    a: 'Yes — AI cuts a 3-hour listing prep job down to ~10 minutes. The win is consistency: you can produce polished photos, descriptions, video, and a microsite for every listing, not just the premium ones.',
  },
];

export default function RealEstateMarketingIdeas() {
  const navigate = useNavigate();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: '12 Real Estate Marketing Ideas That Actually Work in 2026',
      description: '12 practical, modern real estate marketing ideas — from AI photo enhancement to QR-code yard signs — with tools to execute each one.',
      author: { '@type': 'Organization', name: 'ListBright' },
      publisher: { '@type': 'Organization', name: 'ListBright' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <Layout>
      <PageMeta
        title="12 Real Estate Marketing Ideas That Work in 2026"
        description="12 modern real estate marketing ideas agents are using in 2026 — from AI photo enhancement to QR yard signs, slideshow video, and microsites. With free tools."
        canonical="https://listbright.ai/real-estate-marketing-ideas"
        breadcrumbs={[{ label: 'Real Estate Marketing Ideas', path: '/real-estate-marketing-ideas' }]}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container max-w-4xl mx-auto px-4 relative text-center space-y-6">
          <Badge variant="outline" className="px-4 py-1.5 text-sm">
            <Lightbulb className="w-3.5 h-3.5 mr-1.5" /> 2026 Playbook
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            12 Real Estate <span className="text-primary">Marketing Ideas</span> That Work in 2026
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Practical, modern, low-budget — and most of them executable in under 10 minutes per listing.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button asChild size="lg"><a href="#ideas">Read the Ideas <ArrowRight className="w-4 h-4 ml-2" /></a></Button>
            <Button asChild size="lg" variant="outline"><a href="#try">Try the Tools Free</a></Button>
          </div>
        </div>
      </section>

      <section id="ideas" className="py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ideas.map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="border-border/50">
                <CardContent className="p-6 space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="try" className="py-16 bg-muted/30">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="mb-3"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Free Demo</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Execute Half These Ideas in One Upload</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Drop a photo. Get an enhanced version + a description + a styled snippet — in seconds. No signup.
            </p>
          </div>
          <TryItNowWidget onGetStarted={() => navigate('/auth')} />
        </div>
      </section>

      <section className="py-20">
        <div className="container max-w-4xl mx-auto px-4 text-center space-y-6">
          <Badge className="mb-2"><Crown className="w-3.5 h-3.5 mr-1.5" /> Full Kit</Badge>
          <h2 className="text-3xl md:text-4xl font-bold">
            One Tool, 8 of the 12 Ideas — Done
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            ListBright handles enhancement, staging, video, microsite, descriptions, translation,
            Stories format, and QR-ready links — all from a single listing upload.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild size="lg">
              <Link to="/single-property-kit">
                Single Property Kit – $15 <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">See All Plans</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <Card key={f.q}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{f.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}